// src/components/Header/navHelpers.js
// Helpers compartilhados de navegação entre o menu desktop (NavMenu)
// e o menu mobile (MobileMenu). Centraliza a lógica de scroll suave para
// seções da home e a navegação entre páginas (ex.: clicar em "Sobre"
// estando na página do blog leva para a home e rola até a seção).
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

// Extrai o id de âncora de um path. Ex.: "/#sobre" -> "sobre", "/blog" -> null
export function getHashId(path = "") {
    const hashIndex = path.indexOf("#");
    return hashIndex === -1 ? null : path.slice(hashIndex + 1);
}

export function getHeaderOffset() {
    if (typeof window === "undefined") return 80;
    const root = document.documentElement;
    const raw = getComputedStyle(root).getPropertyValue("--header-h").trim();
    const n = parseInt(raw.replace("px", ""), 10);
    return (Number.isFinite(n) ? n : 72) + 8; // gap extra
}

export function prefersReducedMotion() {
    return (
        typeof window !== "undefined" &&
        window.matchMedia &&
        window.matchMedia("(prefers-reduced-motion: reduce)").matches
    );
}

// Rola até a seção com o id informado, compensando a altura do header.
// Retorna false quando o elemento ainda não existe no DOM.
export function scrollToId(id, behavior = "smooth") {
    if (typeof document === "undefined") return false;
    const el = document.getElementById(id);
    if (!el) return false;

    const y =
        el.getBoundingClientRect().top + window.pageYOffset - getHeaderOffset();
    window.scrollTo({
        top: y,
        behavior: prefersReducedMotion() ? "auto" : behavior,
    });

    // atualiza hash sem poluir o histórico
    if (typeof window !== "undefined" && window.history?.replaceState) {
        window.history.replaceState(null, "", `#${id}`);
    }

    // foco acessível no alvo, sem deslocar o scroll
    const prevTabIndex = el.getAttribute("tabindex");
    const hadTabIndex = prevTabIndex !== null;
    if (!hadTabIndex) el.setAttribute("tabindex", "-1");
    el.focus({ preventScroll: true });
    if (!hadTabIndex) setTimeout(() => el.removeAttribute("tabindex"), 250);

    return true;
}

// Hook que devolve um handler de clique para itens de navegação.
// - Links sem hash (ex.: "/blog") navegam normalmente e sobem ao topo.
// - Links com hash (ex.: "/#contato") rolam até a seção quando já estamos
//   na home; caso contrário navegam para a home preservando o hash, e o
//   efeito de hash da própria home cuida do scroll após a montagem.
export function useNavClick(onItemClick) {
    const navigate = useNavigate();

    return useCallback(
        (e, path) => {
            if (e) e.preventDefault();

            const hashId = getHashId(path);

            if (!hashId) {
                navigate(path);
                if (typeof window !== "undefined") {
                    window.scrollTo({ top: 0, behavior: "auto" });
                }
                if (onItemClick) onItemClick(path);
                return;
            }

            const onHome =
                typeof window !== "undefined" &&
                window.location.pathname === "/";

            if (onHome) {
                scrollToId(hashId);
            } else {
                // navega para a home com o hash; a home rola até a seção
                navigate(`/#${hashId}`);
            }

            if (onItemClick) onItemClick(path);
        },
        [navigate, onItemClick]
    );
}
